import { Link, Outlet, createRootRoute, useNavigate, useRouterState } from '@tanstack/react-router';
import { Component, type PropsWithChildren, useEffect, useRef, useState } from 'react';
import { useMe, useSignOut } from '../auth';
import { useLocale } from '../locale';
import { m } from '../paraglide/messages';

export const rootRoute = createRootRoute({
  component: RootLayout,
});

function RootLayout() {
  const me = useMe();
  const path = useRouterState({ select: (s) => s.location.pathname });

  return (
    <div className="shell">
      <a href="#main-content" className="skip-link">
        {m.skipToContent()}
      </a>
      <header className="topbar">
        <div className="brand">
          <Link to="/" className="brand-link">
            {m.appBrand()}
          </Link>
        </div>
        <nav className="nav" aria-label={m.primaryNavAria()}>
          <LocaleToggle />
          {me.data?.user ? (
            <>
              <Link
                to="/cases"
                className={path.startsWith('/cases') ? 'nav-link active' : 'nav-link'}
              >
                {m.navCases()}
              </Link>
              <UserMenu email={me.data.user.email} />
            </>
          ) : path !== '/login' ? (
            <Link to="/login" className="nav-link">
              {m.navSignIn()}
            </Link>
          ) : null}
        </nav>
      </header>
      <main className="main" id="main-content" tabIndex={-1}>
        <RouteErrorBoundary>
          <Outlet />
        </RouteErrorBoundary>
      </main>
    </div>
  );
}

// Global error boundary. Catches anything React throws during render of
// the route tree (e.g. a TanStack Query suspense throw the user didn't
// catch, or a typo in a child component) and shows a recovery card
// instead of the default white-screen. Logs to console so devs can
// still see the stack; surfaces a "reload" affordance to the user.
class RouteErrorBoundary extends Component<PropsWithChildren, { error: Error | null }> {
  override state = { error: null as Error | null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  override componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Surface to console for the dev who's looking; in prod this also
    // bubbles to Sentry-style error trackers if/when they're added.
    console.error('route render error:', error, info);
  }

  override render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="card narrow">
        <h1>Something went wrong</h1>
        <p className="muted">
          The page hit an unexpected error. Reloading usually fixes it; if the problem keeps
          happening, contact support.
        </p>
        <button
          type="button"
          onClick={() => {
            this.setState({ error: null });
            window.location.reload();
          }}
        >
          Reload
        </button>
        <details className="fineprint" style={{ marginTop: 16 }}>
          <summary>Technical details</summary>
          <pre style={{ whiteSpace: 'pre-wrap', fontSize: 12 }}>
            {this.state.error.message}
            {'\n'}
            {this.state.error.stack ?? ''}
          </pre>
        </details>
      </div>
    );
  }
}

// Tiny EN ↔ PT toggle. Single button shows the OTHER language. When the
// app gains more locales we'll switch to a select; for two it's overkill.
function LocaleToggle() {
  const [locale, setLocale] = useLocale();
  const next = locale === 'pt' ? 'en' : 'pt';
  return (
    <button
      type="button"
      className="nav-link locale-toggle"
      onClick={() => setLocale(next)}
      aria-label={next === 'pt' ? 'Mudar para Português' : 'Switch to English'}
      title={next === 'pt' ? 'Mudar para Português' : 'Switch to English'}
    >
      {next.toUpperCase()}
    </button>
  );
}

// Quiet user menu in the top bar. The signed-in email + sign-out used to
// sit inline as plain text and a crappy underlined "Sign out" link, which
// pulled attention to the corner where the user wasn't looking. A trigger
// + popover collapses both into a single muted control.
function UserMenu({ email }: { email: string }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const signOut = useSignOut();
  const navigate = useNavigate();

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('mousedown', onClick);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onClick);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const onSignOut = () => {
    setOpen(false);
    signOut.mutate(undefined, {
      onSuccess: () => navigate({ to: '/login', replace: true }),
    });
  };

  // Initial — first letter of the email, capitalised. Cheap "avatar".
  const initial = email.charAt(0).toUpperCase();

  return (
    <div className="user-menu" ref={wrapRef}>
      <button
        type="button"
        className={`user-menu-trigger ${open ? 'is-open' : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <span className="user-menu-avatar" aria-hidden="true">
          {initial}
        </span>
        <span className="user-menu-caret" aria-hidden="true">
          ▾
        </span>
      </button>
      {open ? (
        <div className="user-menu-popover" role="menu">
          <div className="user-menu-email" title={email}>
            {email}
          </div>
          <Link
            to="/account"
            className="user-menu-item"
            role="menuitem"
            onClick={() => setOpen(false)}
          >
            {m.navAccount()}
          </Link>
          <button
            type="button"
            className="user-menu-item"
            onClick={onSignOut}
            disabled={signOut.isPending}
            role="menuitem"
          >
            {signOut.isPending ? m.navSigningOut() : m.navSignOut()}
          </button>
        </div>
      ) : null}
    </div>
  );
}
